// Message lookup without React, so the rules can be tested and used outside components.
import type { UiLang } from "@imagora/contracts";
import { localeOf } from "../lib/format";

export type MessageValue = string | Record<string, string>;
export type Dictionary = Record<string, MessageValue>;
export type Params = Record<string, string | number>;

const PLACEHOLDER = /\{(\w+)\}/g;

function interpolate(text: string, params: Params | undefined, lang: UiLang): string {
  return text.replace(PLACEHOLDER, (whole, name: string) => {
    const value = params?.[name];
    if (value === undefined) return whole;
    return typeof value === "number" ? new Intl.NumberFormat(localeOf(lang)).format(value) : value;
  });
}

/** `count` picks the plural form by the language's own rules: Romanian has three, Bulgarian and Hungarian two. */
export function formatMessage(message: MessageValue, lang: UiLang, params?: Params): string {
  if (typeof message === "string") return interpolate(message, params, lang);
  const category = new Intl.PluralRules(localeOf(lang)).select(Number(params?.count ?? 0));
  return interpolate(message[category] ?? message.other ?? "", params, lang);
}

export function placeholdersOf(message: MessageValue): string[] {
  const text = typeof message === "string" ? message : Object.values(message).join(" ");
  return [...new Set([...text.matchAll(PLACEHOLDER)].map((m) => m[1]!))].sort();
}

/** For codes that come from the server: unknown ones still read as words rather than as a raw key. */
export function humanizeCode(code: string): string {
  const words = code.replace(/[._-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
